import { NgComponentOutlet } from '@angular/common';
import { CdkDrag, CdkDragEnd, CdkDragHandle } from '@angular/cdk/drag-drop';
import { Component, Input, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { FloatingDialogInstance } from '../../../models/floating-dialog.models';
import { FloatingDialogService } from '../../../services/floating-dialog.service';

@Component({
  selector: 'app-floating-dialog-container',
  standalone: true,
  imports: [
    NgComponentOutlet,
    CdkDrag,
    CdkDragHandle,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
  ],
  templateUrl: './floating-dialog-container.component.html',
  styleUrls: ['./floating-dialog-container.component.scss'],
})
export class FloatingDialogContainerComponent {
  @Input({ required: true }) dialog!: FloatingDialogInstance;

  private readonly service = inject(FloatingDialogService);
  private readonly router = inject(Router);

  /**
   * Route to the full-page view of the loaded component, or `null` if the
   * component hasn't opted in via the `getMainWindowRoute` static. Drives
   * the visibility of the "Open in main window" button in the title bar.
   */
  get mainWindowRoute(): unknown[] | null {
    return (
      this.dialog.component.getMainWindowRoute?.(this.dialog.inputs ?? {}) ??
      null
    );
  }

  onOpenInMainWindow(): void {
    const route = this.mainWindowRoute;
    if (route) {
      this.router.navigate(route);
      this.service.close(this.dialog.id);
    }
  }

  onFocus(): void {
    this.service.focus(this.dialog.id);
  }

  toggleMinimize(): void {
    if (this.dialog.minimized) {
      this.service.restore(this.dialog.id);
    } else {
      this.service.minimize(this.dialog.id);
    }
  }

  onClose(): void {
    this.service.close(this.dialog.id);
  }

  onDragEnded(event: CdkDragEnd): void {
    const offset = event.source.getFreeDragPosition();
    this.service.updatePosition(this.dialog.id, {
      x: this.dialog.position.x + offset.x,
      y: this.dialog.position.y + offset.y,
    });
    event.source.reset();
  }
}
